import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Label } from '@app/ui/label';
import { Tooltip } from '@app/ui/tooltip';

export interface PriorityTagsCellProps {
  readonly tags?: string[];
  /**
   * ai-failure-handling PLAN P5 (ruled 2026-09-24: PARK) — true when the latest colour-scoring
   * attempt for this lead failed (the AI was unavailable and no colour was ever assigned). Only
   * meaningful when `tags` is empty: a lead that already carries a colour tag was scored fine at
   * some point, whatever a LATER attempt did. Undefined/false renders exactly as before (GC's
   * `priorityTags` has no colour-coding concept at all, so this prop stays unset there).
   */
  readonly colorCodeFailed?: boolean;
}

// SAA-82/D11/Q1 — the FE's own copy of the "colour slot" the backend's `PRIORITY_COLOUR_TAGS`
// constant owns (`application-streak.service.ts`). No shared module between the two apps (the
// repo already duplicates `UserRoleEnum` this way, R-new-3) — each file names the other. A colour
// glyph renders as its translated WORD on its matching colour (D11: 🔴 🟠 🟡 🟢 -> Red/Orange/
// Yellow/Green); everything else — `Rematch`, `Psych`, `BMI`, any staff-invented glyph — renders
// its OWN raw Streak option name on a white bordered badge and is never translated (the name is
// the datum). No tag is ever hidden (R9).
const PRIORITY_COLOUR_TAGS: Record<string, { labelKey: string; color: 'error' | 'success' | 'warning' }> = {
  '🔴': { labelKey: 'application.field.priorityTags.colour.red', color: 'error' },
  '🟡': { labelKey: 'application.field.priorityTags.colour.yellow', color: 'warning' },
  '🟢': { labelKey: 'application.field.priorityTags.colour.green', color: 'success' },
};

// Colour chips only — unmapped tags get no dot because they are not colours.
const DOT_ICON = <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor' }} />;

const ORANGE_TAG = '🟠';
// 🟠 has no `Label` palette slot ('warning' is already 🟡's amber); local, no theme.tsx change (O5).
const ORANGE_SX = { color: '#B96A00', bgcolor: '#FFF1E0' };
const UNMAPPED_SX = { color: 'text.primary', bgcolor: 'transparent', border: '1px solid', borderColor: 'grey.500' };

export const PriorityTagsCell = ({ tags, colorCodeFailed }: PriorityTagsCellProps) => {
  const { t } = useTranslation('common');

  if (!tags?.length) {
    // D7-style epistemic-state naming (copied from the watchlist panel's approach, `WatchlistPanel.tsx`):
    // name what is actually known ("not scored, AI unavailable"), never render this the same as a
    // genuine "no colour" or a still-pending one.
    if (colorCodeFailed) {
      return (
        <Tooltip title={t('application.field.priorityTags.parked.tooltip')} placement="top">
          <Label text={t('application.field.priorityTags.parked.label')} sx={UNMAPPED_SX} />
        </Tooltip>
      );
    }

    return <>-</>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {tags.map(tag => {
        const colour = PRIORITY_COLOUR_TAGS[tag];

        if (colour) {
          return <Label key={tag} text={t(colour.labelKey)} color={colour.color} icon={DOT_ICON} />;
        }

        if (tag === ORANGE_TAG) {
          return (
            <Label key={tag} text={t('application.field.priorityTags.colour.orange')} sx={ORANGE_SX} icon={DOT_ICON} />
          );
        }

        return <Label key={tag} text={tag} sx={UNMAPPED_SX} />;
      })}
    </Box>
  );
};
